import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiGet } from '../api';

const POLL_MS = 3000;
const SLOW_AFTER_MS = 90 * 1000; // 90s — surface the "taking longer" notice

export default function Pending() {
  const navigate = useNavigate();
  const [connections, setConnections] = useState(null);
  const [err, setErr] = useState(null);
  const [startedAt] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    let cancelled = false;
    let timer;

    async function tick() {
      try {
        const data = await apiGet('/api/snaptrade/sync-status');
        if (cancelled) return;
        setConnections(data.connections);
        setNow(Date.now());
        if (data.all_done && data.connections.length > 0) {
          // Give the dashboard a beat to see the just-written snapshot row.
          setTimeout(() => navigate('/dashboard', { replace: true }), 500);
          return;
        }
      } catch (e) {
        if (cancelled) return;
        setErr(e.message);
      }
      timer = setTimeout(tick, POLL_MS);
    }

    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [navigate]);

  const elapsed = Math.floor((now - startedAt) / 1000);
  const isSlow = now - startedAt > SLOW_AFTER_MS;
  const hasConns = connections && connections.length > 0;

  return (
    <main className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center px-6">
      <div className="max-w-md w-full space-y-6">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-indigo-500/20 mb-4">
            <Spinner />
          </div>
          <h1 className="text-2xl font-semibold mb-2">Syncing your portfolio</h1>
          <p className="text-sm text-neutral-400">
            We&rsquo;re pulling holdings, balances, and transactions from your
            brokerage. This usually takes 30–90 seconds — your data is ready
            once your custodian finishes responding to SnapTrade.
          </p>
        </div>

        {hasConns ? (
          <div className="rounded border border-neutral-800 bg-neutral-900 divide-y divide-neutral-800">
            {connections.map((c) => (
              <ConnectionRow key={c.id} connection={c} />
            ))}
          </div>
        ) : connections && connections.length === 0 ? (
          <div className="text-sm text-neutral-400 text-center">
            No connections yet — head back and try linking again.
          </div>
        ) : (
          <div className="text-sm text-neutral-500 text-center">Loading…</div>
        )}

        {err && (
          <p className="text-sm text-red-400 text-center">{err}</p>
        )}

        <p className="text-xs text-neutral-500 text-center">
          Elapsed: {elapsed}s
        </p>

        {isSlow && (
          <div className="rounded border border-amber-900/60 bg-amber-950/30 p-4 text-sm text-amber-200 space-y-3">
            <p>
              Taking longer than usual. Your brokerage may still be responding —
              you can wait, or continue and refresh later.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => navigate('/dashboard', { replace: true })}
                className="px-3 py-1.5 rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-100 text-sm"
              >
                Continue to dashboard
              </button>
              <Link
                to="/connect"
                className="px-3 py-1.5 rounded bg-neutral-800 hover:bg-neutral-700 text-neutral-200 text-sm"
              >
                Try reconnecting
              </Link>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}

function ConnectionRow({ connection: c }) {
  const tone = c.last_sync_error
    ? 'error'
    : c.is_pending
      ? 'pending'
      : 'done';
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <div>
        <div className="text-sm font-medium">{c.institution_name || 'Brokerage'}</div>
        <div className="text-xs text-neutral-500">
          {tone === 'pending'
            ? 'Waiting for accounts to load…'
            : tone === 'done'
              ? `${c.accounts_count} account${c.accounts_count === 1 ? '' : 's'} · ${c.holdings_count} holding${c.holdings_count === 1 ? '' : 's'}`
              : c.last_sync_error}
        </div>
      </div>
      <StatusPill tone={tone} />
    </div>
  );
}

function StatusPill({ tone }) {
  if (tone === 'done') {
    return (
      <span className="text-xs px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300">
        Ready
      </span>
    );
  }
  if (tone === 'error') {
    return (
      <span className="text-xs px-2 py-0.5 rounded bg-red-500/20 text-red-300">
        Error
      </span>
    );
  }
  return (
    <span className="text-xs px-2 py-0.5 rounded bg-indigo-500/20 text-indigo-300 inline-flex items-center gap-1.5">
      <Spinner small /> Syncing
    </span>
  );
}

function Spinner({ small = false }) {
  const size = small ? 'w-3 h-3' : 'w-6 h-6';
  return (
    <svg
      className={`${size} animate-spin text-indigo-400`}
      viewBox="0 0 24 24"
      fill="none"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
      />
    </svg>
  );
}
